import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, RefreshCw, Sparkles, X, Mic, CalendarDays, ListChecks, BarChart3, Clock, LayoutGrid, Gift } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { MemberAvatar } from "@/components/Atoms";
import { UnverifiedBanner } from "@/components/UnverifiedBanner";
import { MedicationDosesCard } from "@/components/MedicationDosesCard";
import { useAuth } from "@/context/AuthContext";
import { API_BASE, api, getCached } from "@/lib/api";
import { useRegisterPullToRefresh } from "@/lib/useRegisterPullToRefresh";
import { memberColor } from "@/lib/format";
import { listRecordings, deleteRecording } from "@/lib/voice-store";
import { DaySheet } from "./Plan/DaySheet";

function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Today() {
  const { family, user, members: ctxMembers } = useAuth();
  const navigate = useNavigate();
  // Initialise state from the module-level cache so navigating back to /today
  // is instant — no 10s blank reload. Background refresh runs in load() and
  // updates the screen when fresh data arrives.
  const cachedToday = family ? getCached(`/families/${family.id}/today`) : null;
  const cachedTrials = family ? getCached(`/families/${family.id}/feature-trials/active`) : null;
  const [payload, setPayload] = useState(cachedToday);
  // Members come from AuthContext (single shared roster, no per-screen fetch).
  // Keep a local mirror so existing render code stays untouched; sync via
  // the useEffect below when context updates.
  const [members, setMembers] = useState(ctxMembers || []);
  const [loading, setLoading] = useState(!cachedToday);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kinly:dismissed-nudges") || "[]"); } catch { return []; }
  });
  const [pendingRec, setPendingRec] = useState(null);
  const [daySheetOpen, setDaySheetOpen] = useState(false);
  const [featureTrials, setFeatureTrials] = useState(cachedTrials || []);

  // Detect any unfinished voice recording for this family
  useEffect(() => {
    if (!family?.id) return;
    listRecordings(family.id)
      .then((rows) => {
        const best = rows.find((r) => r.blob && r.blob.size > 0);
        setPendingRec(best || null);
      })
      .catch(() => setPendingRec(null));
  }, [family?.id]);

  const onDiscardPending = async () => {
    if (pendingRec?.id) {
      try { await deleteRecording(pendingRec.id); } catch { /* noop */ }
    }
    setPendingRec(null);
  };

  const load = async () => {
    if (!family) return;
    setRefreshing(true);
    try {
      // Members removed from this batch — sourced from AuthContext now.
      const [t, ft] = await Promise.all([
        api.get(`/families/${family.id}/today`),
        api.get(`/families/${family.id}/feature-trials/active`).catch(() => ({ data: [] })),
      ]);
      setPayload(t.data);
      setMembers(ctxMembers || []);
      setFeatureTrials(ft.data || []);
      // Re-sync iOS / Android local notifications with what's on the
      // calendar + medication-dose roster. Backend has no time-based
      // scheduler, so on-device alarms are the path that actually fires
      // (event-in-1-hour, medication-at-06:30, etc.). Bug reported by
      // the user: "Neither my meeting or medicine fired".
      try {
        const events = t.data?.cards?.find((c) => c.type === "events")?.data || [];
        const doses = (t.data?.cards?.find((c) => c.type === "medication_doses")?.data || [])
          .map((d) => ({ ...d, type: "medication_dose" }));
        const upcoming = t.data?.cards?.find((c) => c.type === "upcoming")?.data || [];
        const all = [...events, ...upcoming, ...doses];
        // Also pull a slightly longer events window so reminders for
        // tomorrow's morning meetings get scheduled today.
        try {
          const horizon = await api.get(`/families/${family.id}/events?days=14`);
          (horizon.data || []).forEach((e) => {
            if (!all.find((x) => x.id === e.id)) all.push(e);
          });
        } catch { /* non-fatal */ }
        const { scheduleAllReminders } = await import("@/lib/localNotifications");
        await scheduleAllReminders(all);
      } catch (err) {
        console.warn("[Kinly] reminder sync failed:", err?.message || err);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onEnableTrial = async (trialId) => {
    try { await api.post(`/families/${family.id}/feature-trials/${trialId}/enable`); }
    catch { /* noop */ }
    setFeatureTrials((arr) => arr.map((t) => t.id === trialId ? { ...t, enabled_at: new Date().toISOString() } : t));
  };
  const onDismissTrial = async (trialId) => {
    setFeatureTrials((arr) => arr.filter((t) => t.id !== trialId));
    try { await api.post(`/families/${family.id}/feature-trials/${trialId}/dismiss`); }
    catch { /* noop */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [family]);
  // Keep local members in sync with AuthContext (e.g. someone invited
  // a new member from Profile — Today's avatar dots update without refetch)
  useEffect(() => { setMembers(ctxMembers || []); }, [ctxMembers]);

  // Register our refresh handler with the AppShell's pull-to-refresh.
  // The shell-level PTR lifts the gesture out of per-screen code and gives
  // every page the same native-feel pulldown. No local ptr binding needed.
  useRegisterPullToRefresh(load);

  if (!family) {
    return (
      <div className="px-6 py-10">
        <p className="text-muted-foreground">Loading your family…</p>
      </div>
    );
  }

  const card = (type) => payload?.cards.find((c) => c.type === type)?.data;
  const briefData = card("brief");
  const meMember = members.find((m) => m.user_id === user?.id);
  const greetingTime = () => {
    const h = new Date().getHours();
    return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
  };
  const greetingFor = () => `Kinly`;
  const events = card("events") || [];
  const doses = card("medication_doses") || [];
  const chores = card("chores") || [];
  const decisions = card("decisions") || [];
  const moments = []; // Recent Moments card removed (Feb 2026, per user feedback — Gallery is reachable from the bottom tab bar). Keep the variable a no-op so the cards-by-key map isn't surprised by missing keys.
  const upcoming = card("upcoming") || [];
  const nudges = card("nudges") || [];
  const visibleNudges = nudges.filter((n) => !dismissed.includes(n.id));
  const dismissNudge = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("kinly:dismissed-nudges", JSON.stringify(next));
  };

  const onNudgeAction = async (n) => {
    const a = n.action || {};
    if (a.type === "promote_todo" && family) {
      try {
        await api.post(`/families/${family.id}/todo-lists/${a.list_id}/items/${a.todo_id}/promote`);
        dismissNudge(n.id);
        load();
      } catch {}
      return;
    }
    if (a.type === "open_chore") { navigate(`/chore/${a.chore_id}`); return; }
    if (a.type === "open_plan") { navigate("/plan"); return; }
    if (a.type === "make_recurring" && family) {
      try {
        await api.patch(`/families/${family.id}/events/${a.item_id}`, {
          recurrence_rule: a.rule || "weekly",
        });
        dismissNudge(n.id);
        load();
      } catch {}
      return;
    }
    if (a.type === "open_memory") {
      navigate("/remember?tab=library");
      return;
    }
    if (a.type === "make_album" && family) {
      try {
        await api.post(`/families/${family.id}/albums`, {
          name: a.name || `Day in pictures · ${a.day}`,
          asset_ids: a.asset_ids || [],
        });
        dismissNudge(n.id);
        load();
        navigate("/remember?tab=albums");
      } catch {}
      return;
    }
  };

  return (
    <>
      <TopBar
        title={family.name}
        subtitle={prettyToday()}
        right={
          <button
            data-testid="today-refresh"
            onClick={load}
            className={`w-10 h-10 flex items-center justify-center text-muted-foreground tap ${refreshing ? "animate-spin" : ""}`}
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.7} />
          </button>
        }
      />

      <main data-testid="surface-today" className="px-4 pt-2 pb-8 space-y-4 relative">
        {loading ? (
          <>
            <div className="kinly-card h-44 skeleton" />
            <div className="kinly-card h-28 skeleton" />
            <div className="kinly-card h-24 skeleton" />
          </>
        ) : (
          <>
            {/* Email verification nudge — soft, dismissible for 24h */}
            <UnverifiedBanner />

            {/* Feature-trial banners (existing-user comp on new paid features) */}
            {featureTrials.map((trial) => (
              <FeatureTrialBanner
                key={trial.id}
                trial={trial}
                onEnable={() => onEnableTrial(trial.id)}
                onDismiss={() => onDismissTrial(trial.id)}
              />
            ))}

            {/* Pending unfinished voice recording banner */}
            {pendingRec && (
              <article
                data-testid="pending-recording-banner"
                className="rounded-2xl p-4 border border-destructive/30 bg-destructive/[0.04] flex items-start gap-3"
              >
                <div className="w-7 h-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shrink-0">
                  <Mic className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-destructive font-medium">Unfinished planning</p>
                  <p className="text-[14px] font-medium mt-0.5">
                    Your voice recording is saved locally
                    {pendingRec.durationMs ? ` (${Math.floor(pendingRec.durationMs/60000)}:${String(Math.floor((pendingRec.durationMs%60000)/1000)).padStart(2,"0")})` : ""}.
                  </p>
                  <p className="text-[13px] text-muted-foreground mt-0.5">
                    Tap "Resume" to upload it to Kinly — or discard.
                  </p>
                  <div className="flex gap-3 mt-2.5">
                    <button
                      data-testid="pending-recording-resume"
                      onClick={() => navigate("/plan")}
                      className="text-[12px] uppercase tracking-wider text-destructive font-medium tap"
                    >
                      Resume in Plan
                    </button>
                    <button
                      data-testid="pending-recording-discard"
                      onClick={onDiscardPending}
                      className="text-[12px] uppercase tracking-wider text-muted-foreground tap"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              </article>
            )}

            {/* Brief — peach gradient in light, subtle peach-on-charcoal in dark */}
            <article
              data-testid="brief-card"
              className="rounded-2xl p-5 border border-primary/15 bg-gradient-to-br from-[hsl(12_69%_98%)] to-[hsl(41_78%_96%)] dark:from-[hsl(12_30%_18%)] dark:to-[hsl(215_13%_16%)] dark:border-primary/25"
            >
              <div className="flex items-center gap-2 mb-2">
                <MemberAvatar member={meMember} name={user?.display_name} size={28} ring={false} />
                <p className="text-[11px] uppercase tracking-[0.22em] text-primary/80 font-medium">
                  {greetingFor()} · {prettyDayName()}
                </p>
              </div>
              <h2 className="font-display text-[22px] font-medium tracking-tight leading-snug">
                {briefData?.headline || (() => {
                  const name = user?.nickname?.trim()
                    || user?.display_name?.split(" ")[0]?.trim();
                  return name
                    ? `Good ${greetingTime()}, ${name}.`
                    : `Good ${greetingTime()}.`;
                })()}
              </h2>
              <p className="mt-3 text-[15px] text-foreground/85 leading-relaxed">
                {briefData?.body}
              </p>
              <button
                data-testid="today-open-day-plan"
                onClick={() => setDaySheetOpen(true)}
                className="mt-4 w-full h-11 rounded-xl bg-foreground text-background text-[13px] font-medium tap inline-flex items-center justify-center gap-2"
              >
                <LayoutGrid className="w-4 h-4" />
                Today's full plan
                <ChevronRight className="w-4 h-4" />
              </button>
            </article>

            {/* Kinly proactive nudges */}
            {visibleNudges.length > 0 && (
              <section data-testid="nudges-card" className="space-y-2">
                {visibleNudges.map((n) => (
                  <article
                    key={n.id}
                    data-testid={`nudge-${n.id}`}
                    className="rounded-2xl p-4 border border-primary/20 bg-primary/[0.04] flex items-start gap-3"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                      <Sparkles className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium">Kinly nudge</p>
                      <p className="text-[14px] font-medium mt-0.5">{n.headline}</p>
                      <p className="text-[13px] text-muted-foreground mt-0.5">{n.body}</p>
                      {(n.asset_ids?.length || 0) > 0 && (
                        <div
                          data-testid={`nudge-photo-strip-${n.id}`}
                          className="flex gap-1.5 overflow-x-auto -mr-3 pr-3 scrollbar-hide mt-2.5"
                        >
                          {n.asset_ids.slice(0, 6).map((aid) => (
                            <button
                              key={aid}
                              data-testid={`nudge-photo-${aid}`}
                              onClick={() => navigate("/remember?tab=library")}
                              className="shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-secondary border border-border tap"
                            >
                              <img
                                src={`${API_BASE}/families/${family.id}/photos/${aid}/raw`}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-3 mt-2.5">
                        <button
                          data-testid={`nudge-action-${n.id}`}
                          onClick={() => onNudgeAction(n)}
                          className="text-[12px] uppercase tracking-wider text-primary font-medium tap"
                        >
                          {n.action?.label}
                        </button>
                        <button
                          data-testid={`nudge-dismiss-${n.id}`}
                          onClick={() => dismissNudge(n.id)}
                          className="text-[12px] uppercase tracking-wider text-muted-foreground tap"
                        >
                          Not now
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => dismissNudge(n.id)}
                      className="w-7 h-7 flex items-center justify-center text-muted-foreground tap shrink-0"
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </article>
                ))}
              </section>
            )}

            {/* Medication doses — show only if any */}
            {doses.length > 0 && (
              <MedicationDosesCard
                doses={doses}
                familyId={family?.id}
                onLogged={load}
                testid="meds-card"
              />
            )}

            {/* Events */}
            <SectionCard
              title="Today"
              empty="Quiet day. Lucky you."
              count={events.length}
              testid="events-card"
              linkTo="/plan"
              icon={CalendarDays}
            >
              {events.map((e) => {
                const participant = members.find((m) => m.id === e.participant_ids?.[0]);
                return (
                  <Row key={e.id} testid={`event-${e.id}`} onClick={() => navigate(`/event/${e.id}`)}>
                    <MemberAvatar member={participant} size={18} ring={false} />
                    <span className="font-mono text-xs tabular-nums text-muted-foreground w-12">
                      {formatTime(e.start_at)}
                    </span>
                    <span className="flex-1 truncate text-[15px]">{e.title}</span>
                  </Row>
                );
              })}
            </SectionCard>

            {/* Chores */}
            <SectionCard
              title="My chores"
              empty="Nothing on your list."
              count={chores.length}
              testid="chores-card"
              linkTo="/plan?tab=chores"
              icon={ListChecks}
            >
              {chores.map((c) => (
                <Row key={c.id} testid={`chore-${c.id}`} onClick={() => navigate(`/chore/${c.id}`)}>
                  <input type="checkbox" className="w-4 h-4 accent-primary" readOnly />
                  <span className="flex-1 truncate text-[15px]">{c.title}</span>
                </Row>
              ))}
            </SectionCard>

            {/* Decisions */}
            {decisions.length > 0 && (
              <SectionCard
                title="Decide"
                count={decisions.length}
                testid="decisions-card"
                linkTo="/plan?tab=polls"
                icon={BarChart3}
              >
                {decisions.map((d) => (
                  <button
                    key={d.id}
                    data-testid={`decision-${d.id}`}
                    onClick={() => navigate(`/poll/${d.id}`)}
                    className="block w-full py-2 text-left tap"
                  >
                    <p className="text-[15px] font-medium">{d.question}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tap to vote
                    </p>
                  </button>
                ))}
              </SectionCard>
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <SectionCard
                title="Next 48h"
                count={upcoming.length}
                testid="upcoming-card"
                linkTo="/plan"
                icon={Clock}
              >
              {upcoming.map((e) => (
                <Row key={e.id} testid={`upcoming-${e.id}`} onClick={() => navigate(`/event/${e.id}`)}>
                  <span className="text-xs text-muted-foreground w-14">
                    {formatRelativeDay(e.start_at)}
                  </span>
                  <span className="flex-1 truncate text-[14px]">{e.title}</span>
                </Row>
              ))}
              </SectionCard>
            )}

            {/* Footer breathing room for orb */}
            <div className="h-4" />
          </>
        )}
      </main>
      <DaySheet
        open={daySheetOpen}
        date={todayIsoDate()}
        members={members}
        onAddEvent={() => {
          setDaySheetOpen(false);
          // Hop to Plan/Calendar with the create-event sheet ready —
          // simplest path that reuses the existing CreateSheet flow without
          // mounting it twice.
          navigate("/plan?tab=calendar&create=1");
        }}
        onClose={() => setDaySheetOpen(false)}
      />
    </>
  );
}

function Header({ title, linkTo, icon: Icon, tint = "primary" }) {
  const tintCls = {
    primary: "bg-primary/12 text-primary",
    accent: "bg-accent/15 text-accent-foreground",
    muted: "bg-secondary text-foreground",
  }[tint] || "bg-primary/12 text-primary";
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && (
          <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center ${tintCls}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
        <h3 className="font-display text-[17px] font-medium tracking-tight">
          {title}
        </h3>
      </div>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-muted-foreground tap p-1 -mr-1"
          aria-label={`Open ${title}`}
        >
          <ChevronRight className="w-5 h-5" strokeWidth={1.6} />
        </Link>
      )}
    </div>
  );
}

function SectionCard({ title, empty, count, testid, linkTo, children, icon: Icon, tint = "primary" }) {
  return (
    <article className="kinly-card" data-testid={testid}>
      <Header title={title} linkTo={linkTo} icon={Icon} tint={tint} />
      <div className="mt-1 divide-y divide-border/70">
        {count === 0 ? (
          empty ? (
            <p className="text-sm text-muted-foreground py-2">{empty}</p>
          ) : null
        ) : (
          children
        )}
      </div>
    </article>
  );
}

function Row({ children, testid, onClick }) {
  if (onClick) {
    return (
      <button
        data-testid={testid}
        onClick={onClick}
        className="w-full flex items-center gap-3 py-2.5 tap text-left"
      >
        {children}
      </button>
    );
  }
  return (
    <div data-testid={testid} className="flex items-center gap-3 py-2.5">
      {children}
    </div>
  );
}

function prettyToday() {
  const d = new Date();
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
}

function prettyDayName() {
  const d = new Date();
  return d.toLocaleDateString(undefined, { weekday: "long" }) + " Brief";
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

function formatRelativeDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if (d.toDateString() === tomorrow.toDateString()) return "Tmrw";
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch {
    return "";
  }
}


function FeatureTrialBanner({ trial, onEnable, onDismiss }) {
  const enabled = !!trial.enabled_at;
  const ann = trial.announcement || {};
  const daysLeft = (() => {
    try {
      const exp = new Date(trial.expires_at).getTime();
      const ms = exp - Date.now();
      return Math.max(0, Math.ceil(ms / 86400000));
    } catch { return 30; }
  })();
  return (
    <article
      data-testid={`feature-trial-${trial.feature_key}`}
      className="rounded-2xl p-4 border border-primary/30 bg-gradient-to-br from-[hsl(12_69%_97%)] to-[hsl(41_78%_95%)] dark:from-[hsl(12_30%_18%)] dark:to-[hsl(215_13%_16%)] dark:border-primary/35 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
        <Gift className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.18em] text-primary font-medium">
          🎁 New · free for 30 days
        </p>
        <h3 className="font-display text-[15px] font-medium mt-0.5 leading-tight">{ann.headline}</h3>
        {ann.body && <p className="text-[13px] text-muted-foreground mt-0.5 leading-snug">{ann.body}</p>}
        <div className="flex gap-3 mt-2 items-center">
          {!enabled ? (
            <>
              <button
                data-testid={`feature-trial-enable-${trial.id}`}
                onClick={onEnable}
                className="h-9 px-4 rounded-full bg-foreground text-background text-[13px] font-medium tap"
              >
                {ann.cta_label || "Try free for 30 days"}
              </button>
              <button
                data-testid={`feature-trial-dismiss-${trial.id}`}
                onClick={onDismiss}
                className="text-[12px] uppercase tracking-wider text-muted-foreground tap"
              >
                No thanks
              </button>
            </>
          ) : (
            <span className="text-[12px] text-primary font-medium tabular-nums">
              ✓ Active · {daysLeft} day{daysLeft === 1 ? "" : "s"} left
            </span>
          )}
        </div>
      </div>
      {!enabled && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="w-7 h-7 flex items-center justify-center text-muted-foreground tap shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </article>
  );
}
